import React, { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import { alpha, Box, useTheme } from '@mui/material';
import { chartsAxisHighlightClasses, chartsGridClasses } from '@mui/x-charts';
import { ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip';
import { getValueToPositionMapper, useDrawingArea, useXScale } from '@mui/x-charts/hooks';
import { LineChart, type LineChartProps } from '@mui/x-charts/LineChart';

import { type ChartData } from '@api/types.ts';
import { useAuthContext } from '@assets/helpers/hooks';
import { ChartGradient, type ChartGradientPT } from '@src/features';

import {
  defaultTooltipFormatter,
  formatLocalAxisKey,
  formatTickDay,
  formatTooltipDate,
  formatTooltipTime,
  type TooltipFormatterArgs,
} from './helpers.ts';

type LineSeriesInput = Omit<NonNullable<LineChartProps['series']>[number], 'data'> & {
  data?: ChartData[];
};

type AxisItem = NonNullable<LineChartProps['highlightedAxis']>;
type ImperativeAxisHighlightHandler = (dataIndex: number | null) => void;

type TooltipContextValue = {
  chartLineColor: string;
  setTooltipElement: (element: HTMLDivElement | null) => void;
};

const LineChartTooltipContext = React.createContext<TooltipContextValue | null>(null);

const CustomLineChartTooltip = () => {
  const context = React.useContext(LineChartTooltipContext);
  const { palette } = useTheme();

  if (!context) {
    return null;
  }

  const { chartLineColor, setTooltipElement } = context;

  return (
    <ChartsTooltipContainer
      trigger="axis"
      placement="bottom-start"
      popperOptions={{
        modifiers: [{ name: 'offset', options: { offset: [8, 8] } }],
      }}
    >
      <Box
        ref={(element) => setTooltipElement(element as HTMLDivElement | null)}
        sx={{
          px: 1.5,
          py: 0.8,
          borderRadius: '14px',
          bgcolor: alpha(palette.white_100 ?? '#fff', 0.88),
          border: `1px solid ${alpha(chartLineColor, 0.35)}`,
          boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
          color: palette.dark,
          fontSize: 16,
          lineHeight: 1.4,
          fontWeight: 500,
          whiteSpace: 'pre-line',
          pointerEvents: 'none',
        }}
      />
    </ChartsTooltipContainer>
  );
};

type CustomLineChartProps = {
  FirstNode?: ReactNode;
  LastNode?: ReactNode;
  gradientProps?: ChartGradientPT;
  lineColor?: string;
  data?: ChartData[];
  extraSeries?: LineSeriesInput[];
  extraGradientProps?: Record<string, ChartGradientPT>;
  skeletonMode?: boolean;
  axisDates?: string[];
  xAxisId?: string;
  onAxisChange?: (axisItems: AxisItem) => void;
  onRegisterAxisHighlight?: (handler: ImperativeAxisHighlightHandler | null) => void;
  tooltipFormatter?: (args: TooltipFormatterArgs) => string;
  seriesValueFormatter?: (value: number | null) => string;
  yAxisValueFormatter?: (value: number) => string;
  yAxisConfig?: LineChartProps['yAxis'];
} & Omit<
  LineChartProps,
  | 'series'
  | 'xAxis'
  | 'yAxis'
  | 'highlightedAxis'
  | 'tooltipAxis'
  | 'onHighlightedAxisChange'
  | 'onTooltipAxisChange'
>;

const buildSeriesData = (
  source: ChartData[] | undefined,
  axisDates: string[],
  timeZone?: string
) => {
  const valueByDate = new Map(
    (source ?? []).map((item) => [formatLocalAxisKey(item.date, timeZone), item.value])
  );

  return axisDates.map((date) => valueByDate.get(date) ?? null);
};

const AxisHighlightMarkers = ({ markerId, color }: { markerId: string; color: string }) => {
  const markerFill = alpha(color, 0.38);

  return (
    <defs>
      <marker
        id={`${markerId}-top`}
        markerWidth="10"
        markerHeight="10"
        refX="5"
        refY="5"
        orient="0"
      >
        <path d="M1 2 L5 8 L9 2 Z" fill={markerFill} />
      </marker>

      <marker
        id={`${markerId}-bottom`}
        markerWidth="10"
        markerHeight="10"
        refX="5"
        refY="5"
        orient="0"
      >
        <path d="M1 8 L5 2 L9 8 Z" fill={markerFill} />
      </marker>
    </defs>
  );
};

/** Renders an MUI-styled SVG highlight once and updates it through an imperative ref. */
const ImperativeAxisHighlight = ({
  axisDates,
  xAxisId,
  onRegister,
}: {
  axisDates: string[];
  xAxisId: string;
  onRegister: (handler: ImperativeAxisHighlightHandler | null) => void;
}) => {
  const { top, height } = useDrawingArea();
  const xScale = useXScale(xAxisId);
  const getXPosition = useMemo(() => getValueToPositionMapper(xScale), [xScale]);
  const pathRef = useRef<SVGPathElement | null>(null);

  const setDataIndex = useCallback(
    (dataIndex: number | null) => {
      const path = pathRef.current;

      if (!path || dataIndex === null) {
        if (path) {
          path.style.opacity = '0';
        }

        return;
      }

      const axisValue = axisDates[dataIndex];

      if (axisValue === undefined) {
        path.style.opacity = '0';
        return;
      }

      const x = getXPosition(axisValue);

      if (!Number.isFinite(x)) {
        path.style.opacity = '0';
        return;
      }

      path.setAttribute('d', `M ${x} ${top} L ${x} ${top + height}`);
      path.style.opacity = '1';
    },
    [axisDates, getXPosition, height, top]
  );

  useEffect(() => {
    onRegister(setDataIndex);

    return () => onRegister(null);
  }, [onRegister, setDataIndex]);

  return (
    <path
      ref={pathRef}
      className={chartsAxisHighlightClasses.root}
      style={{ opacity: 0, pointerEvents: 'none' }}
    />
  );
};

export const CustomLineChart = (props: CustomLineChartProps) => {
  const {
    gradientProps,
    extraSeries,
    extraGradientProps,
    sx,
    FirstNode,
    LastNode,
    lineColor,
    margin,
    skeletonMode = false,
    data,
    axisDates: externalAxisDates,
    xAxisId = 'shared-x-axis',
    onAxisChange,
    onRegisterAxisHighlight,
    tooltipFormatter,
    seriesValueFormatter,
    yAxisValueFormatter,
    yAxisConfig,
    ...rest
  } = props;

  const { palette } = useTheme();
  const { authData } = useAuthContext();
  const timeZone = authData?.timezone;

  const chartLineColor = skeletonMode ? palette.light_dark : (lineColor ?? palette.accent);
  const gradientId = gradientProps?.gradientId;
  const highlightMarkerId = `${gradientId ?? xAxisId}-axis-highlight`;
  const tooltipElementRef = useRef<HTMLDivElement | null>(null);
  const tooltipLabelRef = useRef('');

  const localAxisDates = useMemo(() => {
    const dates = [
      ...(data ?? []).map((item) => formatLocalAxisKey(item.date, timeZone)),
      ...(extraSeries ?? []).flatMap((series) =>
        (series.data ?? []).map((item) => formatLocalAxisKey(item.date, timeZone))
      ),
    ];

    return Array.from(new Set(dates)).sort((a, b) => a.localeCompare(b));
  }, [data, extraSeries, timeZone]);

  const axisDates = externalAxisDates ?? localAxisDates;

  const seriesAll = useMemo(() => {
    const main = {
      id: 'main',
      data: buildSeriesData(data, axisDates, timeZone),
      area: true,
      showMark: false,
      color: chartLineColor,
      valueFormatter:
        seriesValueFormatter ?? ((value: number | null) => (value == null ? '' : String(value))),
    } satisfies NonNullable<LineChartProps['series']>[number];

    const secondary =
      extraSeries?.map((series) => ({
        ...series,
        data: buildSeriesData(series.data, axisDates, timeZone),
        valueFormatter:
          series.valueFormatter ??
          seriesValueFormatter ??
          ((value: number | null) => (value == null ? '' : String(value))),
      })) ?? [];

    return [main, ...secondary];
  }, [data, extraSeries, axisDates, chartLineColor, seriesValueFormatter, timeZone]);

  const [yMin, yMax] = useMemo(() => {
    const allValues = seriesAll.flatMap((series) =>
      (series.data ?? []).filter((value): value is number => typeof value === 'number')
    );

    if (!allValues.length) {
      return [0, 5];
    }

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    const ymin = Math.max(0, Math.floor(min / 5) * 5);
    const ymax = Math.max(5, Math.ceil(max / 5) * 5);

    return [ymin, ymax];
  }, [seriesAll]);

  /** Attaches the rendered MUI tooltip so its text can update without a React state change. */
  const setTooltipElement = useCallback((element: HTMLDivElement | null) => {
    tooltipElementRef.current = element;

    if (element) {
      element.textContent = tooltipLabelRef.current;
    }
  }, []);

  /** Computes and writes the current tooltip label directly to the rendered tooltip node. */
  const updateTooltipLabel = useCallback(
    (axisItems: AxisItem) => {
      const activeAxisItem =
        axisItems.find((item) => String(item.axisId) === String(xAxisId)) ?? axisItems[0];
      const dataIndex = activeAxisItem?.dataIndex;
      const activeAxisValue = typeof dataIndex === 'number' ? axisDates[dataIndex] : undefined;
      let nextTooltipLabel = '';

      if (activeAxisValue && typeof dataIndex === 'number') {
        const value = (seriesAll[0]?.data?.[dataIndex] ?? null) as number | null;
        const timeLabel = formatTooltipTime(activeAxisValue);
        const resolvedTooltipFormatter = tooltipFormatter ?? defaultTooltipFormatter;

        nextTooltipLabel = resolvedTooltipFormatter({
          value,
          axisValue: activeAxisValue,
          timeLabel,
          dataIndex,
        });
      }

      tooltipLabelRef.current = nextTooltipLabel;

      if (tooltipElementRef.current) {
        tooltipElementRef.current.textContent = nextTooltipLabel;
      }
    },
    [axisDates, seriesAll, tooltipFormatter, xAxisId]
  );

  const handleHighlightedAxisChange = useCallback(
    (axisItems: AxisItem) => {
      updateTooltipLabel(axisItems);
      onAxisChange?.(axisItems);
    },
    [onAxisChange, updateTooltipLabel]
  );

  const tooltipContextValue = useMemo(
    () => ({
      chartLineColor,
      setTooltipElement,
    }),
    [chartLineColor, setTooltipElement]
  );

  const defaultYAxis = useMemo<NonNullable<LineChartProps['yAxis']>>(
    () => [
      {
        min: yMin,
        max: yMax,
        tickMinStep: 5,
        tickNumber: 4,
        valueFormatter: yAxisValueFormatter,
      },
    ],
    [yAxisValueFormatter, yMin, yMax]
  );

  const settings: LineChartProps = useMemo(
    () => ({
      series: seriesAll,
      hideLegend: true,
      skipAnimation: true,

      xAxis: [
        {
          id: xAxisId,
          scaleType: 'point',
          data: axisDates,
          valueFormatter: (value, context) => {
            const date = String(value);

            if (context.location === 'tooltip') {
              return formatTooltipDate(date);
            }

            return formatTickDay(date);
          },
        },
      ],

      yAxis: yAxisConfig ?? defaultYAxis,

      margin: {
        top: 20,
        right: 10,
        bottom: 0,
        left: 0,
        ...(typeof margin === 'object' && margin),
      },

      grid: {
        horizontal: true,
      },

      disableAxisListener: skeletonMode,
      axisHighlight:
        skeletonMode || onRegisterAxisHighlight
          ? { x: 'none', y: 'none' }
          : { x: 'line', y: 'none' },

      onHighlightedAxisChange: skeletonMode ? undefined : handleHighlightedAxisChange,

      slots: skeletonMode
        ? undefined
        : {
            tooltip: CustomLineChartTooltip,
          },

      slotProps: {
        tooltip: {
          trigger: skeletonMode ? 'none' : 'axis',
        },
      },

      sx: {
        '& .MuiAreaElement-root[data-series="main"]': {
          fill: gradientId ? `url('#${gradientId}')` : undefined,
          opacity: skeletonMode ? 0.18 : 1,
        },

        ...Object.fromEntries(
          Object.entries(extraGradientProps ?? {}).map(([seriesId, seriesGradient]) => [
            `& .MuiAreaElement-root[data-series="${seriesId}"]`,
            {
              fill: seriesGradient.gradientId ? `url('#${seriesGradient.gradientId}')` : undefined,
              opacity: skeletonMode ? 0.18 : 1,
            },
          ])
        ),

        '& .MuiLineElement-root[data-series="main"]': {
          stroke: chartLineColor,
          strokeWidth: 1,
        },

        [`& .${chartsGridClasses.line}`]: {
          strokeDasharray: '2 2',
        },

        [`& .${chartsGridClasses.horizontalLine}`]: {
          stroke: palette.dark,
          strokeOpacity: 0.15,
        },

        '& g[clip-path] > text': {
          fill: `${palette.light_dark} !important`,
        },

        [`& .${chartsAxisHighlightClasses.root}`]: {
          stroke: alpha(chartLineColor, 0.28),
          strokeDasharray: 'none',
          strokeWidth: 1,
          markerStart: `url(#${highlightMarkerId}-top)`,
          markerEnd: `url(#${highlightMarkerId}-bottom)`,
        },

        '& .MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
          fill: skeletonMode ? 'transparent' : palette.light_dark,
        },

        '& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
          fill: palette.light_dark,
        },

        '& .MuiChartsAxis-line': {
          stroke: 'transparent !important',
        },

        '& .MuiChartsAxis-tick': {
          stroke: 'transparent !important',
        },

        ...sx,
      },
    }),
    [
      defaultYAxis,
      yAxisConfig,
      axisDates,
      chartLineColor,
      gradientId,
      extraGradientProps,
      highlightMarkerId,
      margin,
      handleHighlightedAxisChange,
      onRegisterAxisHighlight,
      palette,
      seriesAll,
      skeletonMode,
      sx,
      xAxisId,
    ]
  );

  return (
    <Box
      sx={{
        minWidth: 0,
        flexGrow: 1,
        height: '100%',
        bgcolor: 'white',
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {FirstNode}

      <LineChartTooltipContext.Provider value={tooltipContextValue}>
        <LineChart {...settings} {...rest}>
          <ChartGradient
            gradientId={gradientProps?.gradientId}
            firstStopColor={gradientProps?.firstStopColor}
            firstStopOpacity={gradientProps?.firstStopOpacity}
            secondStopColor={gradientProps?.secondStopColor}
            secondStopOpacity={gradientProps?.secondStopOpacity}
          />

          {Object.values(extraGradientProps ?? {}).map((seriesGradient) => (
            <ChartGradient
              key={seriesGradient.gradientId}
              gradientId={seriesGradient.gradientId}
              firstStopColor={seriesGradient.firstStopColor}
              firstStopOpacity={seriesGradient.firstStopOpacity}
              secondStopColor={seriesGradient.secondStopColor}
              secondStopOpacity={seriesGradient.secondStopOpacity}
            />
          ))}

          <AxisHighlightMarkers markerId={highlightMarkerId} color={chartLineColor} />

          {!skeletonMode && onRegisterAxisHighlight ? (
            <ImperativeAxisHighlight
              axisDates={axisDates}
              xAxisId={xAxisId}
              onRegister={onRegisterAxisHighlight}
            />
          ) : null}
        </LineChart>
      </LineChartTooltipContext.Provider>

      {LastNode}
    </Box>
  );
};
